import { ClaudeMigrationEngine, ClaudeMigrationInstruction } from '../claude-migration-engine';
import { MigrationInstructionGenerator, MigrationTemplateConfig } from '../migration-instruction-generator';
import { Migration } from '../types';

/**
 * Dezzy v3.0 Tailwind Migration
 * 
 * This migration converts existing projects to use Tailwind CSS as the primary styling solution.
 * It's designed to be run on projects created with earlier versions that used custom CSS or other styling solutions.
 */

export const TAILWIND_MIGRATION_CONFIG: MigrationTemplateConfig = {
  fromVersion: '1.x',
  toVersion: '1.2.6',
  changes: {
    dependencies: {
      add: [
        { name: 'tailwindcss', version: '^3.4.0' },
        { name: '@tailwindcss/forms', version: '^0.5.0' },
        { name: '@tailwindcss/typography', version: '^0.5.0' },
        { name: 'autoprefixer', version: '^10.4.0', dev: true },
        { name: 'postcss', version: '^8.4.0', dev: true }
      ],
      remove: [
        'styled-components',
        '@emotion/react', 
        '@emotion/styled',
        'css-modules'
      ],
      update: []
    },
    breakingChanges: {
      removedApis: [
        'styled.*',
        'css`` template literals',
        'makeStyles',
        'useStyles'
      ],
      changedApis: [
        {
          old: 'className={styles.button}',
          new: 'className="bg-blue-500 hover:bg-blue-600 px-4 py-2"',
          reason: 'CSS modules replaced with Tailwind utilities'
        }
      ],
      newRequirements: [
        'Tailwind CSS must be configured via tailwind.config.js',
        'PostCSS configuration required for Tailwind processing',
        'Design tokens must be mapped to Tailwind theme'
      ]
    },
    newFeatures: {
      framework: 'tailwind',
      designTokens: true,
      typescript: true
    },
    codeTransforms: [
      {
        name: 'Convert CSS Classes to Tailwind',
        description: 'Replace custom CSS classes and CSS modules with Tailwind utility classes',
        filePatterns: ['src/**/*.{tsx,ts,jsx,js}', 'src/**/*.css'],
        complexity: 'high'
      },
      {
        name: 'Migrate Design Tokens',
        description: 'Convert Dezzy design tokens to Tailwind theme configuration',
        filePatterns: ['src/tokens/**/*', 'tailwind.config.js'],
        complexity: 'medium'
      },
      {
        name: 'Update Component Styling',
        description: 'Replace styled-components and emotion with Tailwind classes',
        filePatterns: ['src/components/**/*.{tsx,ts}'],
        complexity: 'high'
      }
    ]
  }
};

/**
 * Generate the Tailwind migration instruction
 */
export async function createTailwindMigrationInstruction(): Promise<ClaudeMigrationInstruction> {
  const generator = new MigrationInstructionGenerator();
  
  // Ensure templates exist
  await generator.createDefaultTemplates();
  
  // Generate instructions
  const instructions = await generator.generateInstructions(TAILWIND_MIGRATION_CONFIG);
  
  // Find the Tailwind-specific instruction
  const tailwindInstruction = instructions.find(i => i.id.includes('tailwind-migration'));
  
  if (!tailwindInstruction) {
    throw new Error('Failed to generate Tailwind migration instruction');
  }

  // Enhance with Dezzy-specific customizations
  return {
    ...tailwindInstruction,
    prompt: `${tailwindInstruction.prompt}

## Dezzy-Specific Requirements:

### Design Token Integration
- Convert existing Dezzy tokens in \`src/tokens/\` to Tailwind theme values
- Map color tokens to Tailwind color palette
- Convert spacing tokens to Tailwind spacing scale  
- Transform typography tokens to Tailwind font configuration

### Component Patterns
- Maintain Dezzy component APIs (props should remain the same)
- Keep variant-based styling approach but implement with Tailwind
- Preserve responsive behavior using Tailwind breakpoints
- Maintain accessibility attributes and ARIA compliance

### File Structure Changes
- Remove \`*.module.css\` files and replace with Tailwind classes
- Remove styled-component definitions
- Keep component file structure but update styling approach
- Create new \`tailwind.config.js\` with Dezzy theme

### Code Examples

**Before (CSS Modules):**
\`\`\`tsx
import styles from './Button.module.css';

export const Button = ({ variant = 'primary', size = 'md', children }) => {
  return (
    <button className={\`\${styles.button} \${styles[variant]} \${styles[size]}\`}>
      {children}
    </button>
  );
};
\`\`\`

**After (Tailwind):**
\`\`\`tsx
import { cn } from '@/lib/utils';

const buttonVariants = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900',
  outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700'
};

const buttonSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base', 
  lg: 'px-6 py-3 text-lg'
};

export const Button = ({ variant = 'primary', size = 'md', children, className, ...props }) => {
  return (
    <button 
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
        buttonVariants[variant],
        buttonSizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};
\`\`\`

**Tailwind Config Integration:**
\`\`\`js
// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      colors: {
        // Map from src/tokens/colors.ts
        primary: {
          50: '#eff6ff',
          500: '#3b82f6',
          900: '#1e3a8a'
        }
      },
      spacing: {
        // Map from src/tokens/spacing.ts  
        '18': '4.5rem',
        '88': '22rem'
      }
    }
  }
};
\`\`\`

### Validation
- Ensure no inline styles remain (\`style={}\`)
- Verify all CSS imports are removed
- Check that all components render correctly
- Confirm responsive behavior works with Tailwind breakpoints
- Validate that design tokens are properly mapped to Tailwind theme
    `.trim(),

    validationRules: [
      ...(tailwindInstruction.validationRules || []),
      {
        name: 'no-styled-components',
        pattern: /styled\./,
        message: 'styled-components usage should be replaced with Tailwind classes',
        severity: 'error'
      },
      {
        name: 'no-css-modules',
        pattern: /import\s+\w+\s+from\s+['"][^'"]*\.module\.css['"]/,
        message: 'CSS modules should be replaced with Tailwind utilities',
        severity: 'error'
      },
      {
        name: 'has-tailwind-config',
        pattern: /module\.exports\s*=\s*\{[\s\S]*theme:/,
        message: 'tailwind.config.js should include custom theme configuration',
        severity: 'warning'
      }
    ]
  };
}

/**
 * Create the complete Tailwind migration for Dezzy v3
 */
export async function createTailwindMigration(claudeEngine?: ClaudeMigrationEngine): Promise<Migration> {
  const engine = claudeEngine || new ClaudeMigrationEngine();
  const instruction = await createTailwindMigrationInstruction();
  
  return engine.createMigration(instruction);
}

/**
 * Helper function to generate migration instructions for a release
 */
export async function generateV3MigrationInstructions(outputDir: string): Promise<void> {
  const generator = new MigrationInstructionGenerator();
  await generator.createDefaultTemplates();
  
  const instructions = await generator.generateInstructions(TAILWIND_MIGRATION_CONFIG);
  await generator.saveInstructions(instructions, outputDir);
  
  console.log(`Generated ${instructions.length} migration instructions in ${outputDir}`);
  console.log('Instructions:', instructions.map(i => `- ${i.title}`).join('\n'));
}